import { createSlice, PayloadAction, current } from '@reduxjs/toolkit'
import { ItemStatus } from '@engyalo/delivery-ui-components/lib/interfaces/enums'
import {
  SubItem,
  SubItems,
} from '@engyalo/delivery-ui-components/lib/interfaces/catalog'
import type {
  ICatalog,
  ICatalogItem,
  ICategory,
  ICategoryPagination,
  IFreeGood,
  IFreegoodsConf,
  IFreegoodsQuantitySelection,
  IProduct,
  IPromotionShortcut,
  IPromotionsAvailability,
  ISearchResult,
  TBundle,
} from '../../../interfaces'
import {
  setCatalogData,
  setCatalogPagination,
} from '../../../utils/categoryUtils'
import { PromotionSelectionCriteria } from '../../../graphQL/commons'
import {
  markCategoriesSyncing,
  markRowsSyncing,
  setSkuSyncing,
} from '../../skuSyncing'

export const initialState: ICatalog = {
  categories: [],
  categoryList: [],
  search: {
    term: '',
    items: [],
    status: 'OK',
    loading: false,
    totalSearchStartCount: 0,
  },
  isFetchingMore: false,
  pagination: {},
  synchronizingCart: false,
  disabledAddOnBundle: false,
  currentBundleCount: 1,
  bundleCompleted: false,
  freegoodsConf: {
    isConfirmDisabled: true,
    freegoods: [],
    selectBy: PromotionSelectionCriteria.CUSTOMER_SELECTION,
    quantitySelection: undefined,
  },
  currentBundlePrice: null,
  currentBundlePriceValue: 0,
  currentBundleSubtotal: 0,
  repricing: false,
  repriced: false,
}

export const catalogSlice = createSlice({
  name: 'catalogSlice',
  initialState,
  reducers: {
    setCategoriesPagination: (
      state,
      action: PayloadAction<ICategoryPagination>,
    ) => {
      state.categoriesPagination = action.payload
    },
    setCurrentCategory: (state, action: PayloadAction<ICatalogItem>) => {
      state.currentCategory = action.payload
    },
    setCategories: (state, action: PayloadAction<ICatalogItem[]>) => {
      state.categories = action.payload
    },
    setCategoriesAndPagination: (
      state,
      {
        payload: { categories, paginationSize },
      }: PayloadAction<{
        categories: ICatalogItem[]
        paginationSize?: number
      }>,
    ) => {
      state.categories = setCatalogData(categories)
      state.pagination = setCatalogPagination(categories, paginationSize)
    },
    setCategoryList: (state, action: PayloadAction<ICategory[]>) => {
      state.categoryList = action.payload
    },
    setSynchronizingCart: (state, action: PayloadAction<boolean>) => {
      state.synchronizingCart = action.payload
    },
    setFetchingMoreLoading: (state, action: PayloadAction<boolean>) => {
      state.isFetchingMore = action.payload
    },
    setNewPagination: (state) => {
      state.pagination[state.currentCategory?.slug || ''].pageNumber += 1
    },
    setPaginationAsCompleted: (state) => {
      state.pagination[state.currentCategory?.slug || ''].hasMore = false
    },
    setCurrentBundle: (
      state,
      { payload }: PayloadAction<{ selections: TBundle[]; completed: boolean }>,
    ) => {
      const { completed, selections } = payload
      const reducedSelections =
        selections.length > 0
          ? selections.reduce((acc, obj) => ({
              ...acc,
              ...obj,
            }))
          : {}
      const { currentBundleCount = 1 } = state
      state.disabledAddOnBundle = !(currentBundleCount > 0 && completed)
      state.bundlesCount = reducedSelections
      state.bundleCompleted = completed
    },
    setSubItem: (state, { payload }: PayloadAction<SubItem | null>) => {
      state.subItemEdit = payload
    },
    setSubItems: (state, { payload }: PayloadAction<SubItems>) => {
      state.subItemsList = payload
    },
    setProductDetail: (
      state,
      action: PayloadAction<{
        product: IProduct
        subItem?: SubItem
        /**
         * Set by callers that refresh the product already on screen. They
         * rebuild it from a render snapshot of the store, so the incoming
         * product carries whatever `isSyncing` that snapshot had; keeping the
         * live value instead stops a cart response landing mid-mutation from
         * erasing the flag before the Counter sees the edge that resyncs it.
         * Left unset when opening a product, so the flag always starts clean
         * and a mutation that never settled cannot outlive the screen.
         */
        keepSyncing?: boolean
      }>,
    ) => {
      const { product, subItem, keepSyncing } = action.payload
      const isSyncing =
        keepSyncing && state.selectedProduct?.sku === product.sku
          ? state.selectedProduct.isSyncing
          : undefined
      const item: IProduct = {
        ...product,
        isBundle:
          !!product.bundle &&
          !!product.bundle.items &&
          product.bundle.items.length > 0,
        subItem,
        isSyncing,
      }
      state.selectedProduct = item
      state.disabledAddOnBundle = !subItem
      state.bundlesCount = subItem?.items
      state.currentBundleCount = subItem?.quantity || 1
      state.bundleCompleted = !!subItem
      state.currentBundlePrice = ''
      state.currentBundlePriceValue = 0
    },
    setCurrentBundleCount: (
      state,
      { payload: newCount }: PayloadAction<number>,
    ) => {
      const { bundleCompleted = false } = state
      const disabledButton = newCount > 0 && bundleCompleted
      state.currentBundleCount = newCount
      state.disabledAddOnBundle = !disabledButton
    },
    clearProductDetail: (state) => {
      state.selectedProduct = undefined
      // So a return to the same product cannot show the previous visit's
      // verdict before its own lookup answers.
      delete state.promotionsAvailability
    },
    clearCatalogueCart: (state) => {
      const categories = current(state.categories)
      state.categories = categories.map((category: ICatalogItem) => {
        const productsPrices = category.productsPrices
          ? category.productsPrices.map((item) => {
              const newValue = { ...item, quantity: 0 }
              return newValue
            })
          : []
        const newItem = {
          ...category,
          productsPrices,
        }
        return newItem
      })
    },
    updateSearchData: (state, action: PayloadAction<ISearchResult>) => {
      const { term, items = [], loading, status, counter } = action.payload
      state.search = {
        ...state.search,
        term: term || state.search.term,
        items,
        loading,
        status,
        counter,
      }
    },
    clearSearchData: (state) => {
      const { term = '', items } = state.search
      state.search = {
        term: '',
        items: term.trim() ? [] : items,
        loading: false,
        counter: 0,
        status: 'OK',
        totalSearchStartCount: state.search.totalSearchStartCount, // I think this should't be reset
      }
    },
    setRecommendedProducts: (state, { payload }: PayloadAction<IProduct[]>) => {
      state.recommendedProducts = payload
    },
    setCategorySelectedStatus: (
      state,
      { payload }: PayloadAction<ItemStatus>,
    ) => {
      state.categorySelectedStatus = payload
    },
    setFreegoodsConf: (state, { payload }: PayloadAction<IFreegoodsConf>) => {
      state.freegoodsConf = payload
    },
    setAvailableFreegoods: (
      state,
      {
        payload: { isConfirmDisabled, freegoods },
      }: PayloadAction<{ freegoods: IFreeGood[]; isConfirmDisabled: boolean }>,
    ) => {
      state.freegoodsConf.freegoods = freegoods
      state.freegoodsConf.isConfirmDisabled = isConfirmDisabled
    },
    setQuantitySelection: (
      state,
      {
        payload: { quantitySelection, isConfirmDisabled },
      }: PayloadAction<{
        quantitySelection: IFreegoodsQuantitySelection
        isConfirmDisabled: boolean
      }>,
    ) => {
      state.freegoodsConf.quantitySelection = quantitySelection
      state.freegoodsConf.isConfirmDisabled = isConfirmDisabled
    },
    setCurrentBundlePrice: (state, { payload }: PayloadAction<string>) => {
      state.currentBundlePrice = payload
    },
    setCurrentBundlePriceValue: (state, { payload }: PayloadAction<number>) => {
      state.currentBundlePriceValue = payload
    },
    setCurrentBundleSubtotal: (state, { payload }: PayloadAction<number>) => {
      state.currentBundleSubtotal = payload
    },
    setCurrentCategoryCatalog: (state, { payload }: PayloadAction<string>) => {
      state.currentCategoryCatalog = payload
    },
    setRepricing: (state, { payload }: PayloadAction<boolean>) => {
      state.repricing = payload
    },
    /**
     * Records that `refreshCatalog` wrote prices the Apollo cache does not
     * hold (COM-1520), so load-more reads past the cache from then on.
     */
    setRepriced: (state) => {
      state.repriced = true
    },
    /**
     * Stores the stock lookup's verdict and swaps in the shortcuts recomputed
     * from it, only while it is still about the product on screen; a verdict
     * for any other product is dropped whole. Touches nothing else on the
     * product, so a bundle selection in progress survives, and leaves the
     * shortcuts alone when none were recomputed.
     */
    setPromotionsAvailability: (
      state,
      {
        payload: { availability, promotionsShortcuts },
      }: PayloadAction<{
        availability: IPromotionsAvailability
        promotionsShortcuts?: IPromotionShortcut[]
      }>,
    ) => {
      if (state.selectedProduct?.sku !== availability.sku) return
      state.promotionsAvailability = availability
      if (promotionsShortcuts) {
        state.selectedProduct.promotionsShortcuts = promotionsShortcuts
      }
    },
  },
  // `search.items` is left out on purpose: its quantities are baked in when
  // the search answers and no cart response rewrites them. See `setSkuSyncing`.
  // `currentCategory` is a copy of a category that may carry a mark, but no
  // default binding renders its rows; it is read for its slug and name only.
  extraReducers: (builder) => {
    builder.addCase(setSkuSyncing, (state, { payload }) => {
      markCategoriesSyncing(state.categories, payload)
      markRowsSyncing(state.recommendedProducts, payload)
      if (state.selectedProduct) {
        markRowsSyncing([state.selectedProduct], payload)
      }
    })
  },
})

export const {
  setNewPagination,
  setFetchingMoreLoading,
  setPaginationAsCompleted,
  setCategories,
  setCategoriesAndPagination,
  updateSearchData,
  clearSearchData,
  setProductDetail,
  clearProductDetail,
  clearCatalogueCart,
  setCurrentCategory,
  setSynchronizingCart,
  setCategoryList,
  setCategoriesPagination,
  setRecommendedProducts,
  setCategorySelectedStatus,
  setCurrentBundle,
  setCurrentBundleCount,
  setSubItem,
  setSubItems,
  setFreegoodsConf,
  setAvailableFreegoods,
  setCurrentBundlePrice,
  setCurrentBundlePriceValue,
  setCurrentBundleSubtotal,
  setCurrentCategoryCatalog,
  setRepricing,
  setRepriced,
  setQuantitySelection,
  setPromotionsAvailability,
} = catalogSlice.actions

export default catalogSlice.reducer
